/**
 * HardBurnConfirmation
 *
 * Shown when outputMode === 'hard_burn'.
 * Contains:
 * 1. Style editor (font, color, size, outline, shadow, position, margin)
 * 2. Live style preview via SubtitleStylePreview
 * 3. Cost confirmation checkbox (gate before generate)
 *
 * All state is managed by SubtitlesContext — no local state here.
 */

import { memo, useCallback, useId } from 'react';
import { Flame, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';
import { SubtitleStylePreview } from './SubtitleStylePreview';
import type { SubtitleStyle } from '../types';

// ============================================================================
// Style field editor helpers
// ============================================================================

interface ColorInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}

const ColorInput = memo(function ColorInput({ label, value, onChange, disabled }: ColorInputProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-white/60">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-8 h-8 rounded border border-white/20 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <span className="text-xs text-white/50 font-mono">{value.toUpperCase()}</span>
      </div>
    </div>
  );
});

interface SliderInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
  disabled: boolean;
}

const SliderInput = memo(function SliderInput({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  disabled,
}: SliderInputProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-xs text-white/60">{label}</label>
        <span className="text-xs text-white/50 font-mono">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="w-full accent-purple-500 disabled:opacity-50"
      />
    </div>
  );
});

// ============================================================================
// Main component
// ============================================================================

export const HardBurnConfirmation = memo(function HardBurnConfirmation() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleStyleChange, handleHardBurnConfirmedChange } =
    useSubtitles();
  const { style, hardBurnConfirmed } = state;

  const checkboxId = useId();
  const interactionDisabled = isUploading || isGenerating;

  const updateStyle = useCallback(
    (patch: Partial<SubtitleStyle>) => {
      handleStyleChange({ ...style, ...patch });
    },
    [style, handleStyleChange]
  );

  return (
    <div className="space-y-4">
      {/* ------------------------------------------------------------------ */}
      {/* Style editor                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div className="panel-glass">
        <h3 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-orange-400" />
          <span>{t('subtitles.hardBurn.styleTitle', { defaultValue: 'Subtitle Style' })}</span>
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Font family */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-white/60">
              {t('subtitles.hardBurn.fontFamily', { defaultValue: 'Font' })}
            </label>
            <select
              value={style.font_family}
              onChange={(e) => updateStyle({ font_family: e.target.value })}
              disabled={interactionDisabled}
              className="input text-sm py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {['Arial', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Georgia', 'Verdana'].map(
                (f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                )
              )}
            </select>
          </div>

          {/* Position */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-white/60">
              {t('subtitles.hardBurn.position', { defaultValue: 'Position' })}
            </label>
            <select
              value={style.position}
              onChange={(e) =>
                updateStyle({ position: e.target.value as SubtitleStyle['position'] })
              }
              disabled={interactionDisabled}
              className="input text-sm py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="bottom">
                {t('subtitles.hardBurn.positionBottom', { defaultValue: 'Bottom' })}
              </option>
              <option value="middle">
                {t('subtitles.hardBurn.positionMiddle', { defaultValue: 'Middle' })}
              </option>
              <option value="top">
                {t('subtitles.hardBurn.positionTop', { defaultValue: 'Top' })}
              </option>
            </select>
          </div>

          {/* Colors */}
          <ColorInput
            label={t('subtitles.hardBurn.textColor', { defaultValue: 'Text Color' })}
            value={style.primary_color}
            onChange={(v) => updateStyle({ primary_color: v })}
            disabled={interactionDisabled}
          />
          <ColorInput
            label={t('subtitles.hardBurn.outlineColor', { defaultValue: 'Outline Color' })}
            value={style.outline_color}
            onChange={(v) => updateStyle({ outline_color: v })}
            disabled={interactionDisabled}
          />

          {/* Sliders — full-width on their own row */}
          <div className="sm:col-span-2">
            <SliderInput
              label={t('subtitles.hardBurn.fontSize', { defaultValue: 'Font Size' })}
              value={style.font_size}
              min={16}
              max={96}
              step={2}
              unit="pt"
              onChange={(v) => updateStyle({ font_size: v })}
              disabled={interactionDisabled}
            />
          </div>

          <div className="sm:col-span-2">
            <SliderInput
              label={t('subtitles.hardBurn.outlineWidth', { defaultValue: 'Outline Width' })}
              value={style.outline_width}
              min={0}
              max={8}
              step={0.5}
              unit="px"
              onChange={(v) => updateStyle({ outline_width: v })}
              disabled={interactionDisabled}
            />
          </div>

          <div className="sm:col-span-2">
            <SliderInput
              label={t('subtitles.hardBurn.shadowDepth', { defaultValue: 'Shadow Depth' })}
              value={style.shadow_depth}
              min={0}
              max={8}
              step={0.5}
              unit="px"
              onChange={(v) => updateStyle({ shadow_depth: v })}
              disabled={interactionDisabled}
            />
          </div>

          <div className="sm:col-span-2">
            <SliderInput
              label={t('subtitles.hardBurn.marginV', { defaultValue: 'Vertical Margin' })}
              value={style.margin_v}
              min={0}
              max={200}
              step={4}
              unit="px"
              onChange={(v) => updateStyle({ margin_v: v })}
              disabled={interactionDisabled}
            />
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Live preview                                                        */}
      {/* ------------------------------------------------------------------ */}
      <SubtitleStylePreview style={style} />

      {/* ------------------------------------------------------------------ */}
      {/* Cost confirmation checkbox                                          */}
      {/* ------------------------------------------------------------------ */}
      <div
        className={[
          'rounded-lg border p-4 sm:p-5 transition-colors',
          hardBurnConfirmed
            ? 'border-orange-500/40 bg-orange-900/10'
            : 'border-white/10 bg-black/20',
        ].join(' ')}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">
              {t('subtitles.hardBurn.costWarningTitle', {
                defaultValue: 'High rendering cost',
              })}
            </p>
            <p className="text-xs text-white/60 mt-1">
              {t('subtitles.hardBurn.costWarningBody', {
                defaultValue:
                  'Hard burn-in re-encodes the entire video on GPU (libx264). This is significantly more expensive than sidecar or soft-embedded modes. One render per language is required.',
              })}
            </p>

            <label
              className={[
                'flex items-center gap-2.5 mt-3 cursor-pointer min-h-[44px]',
                interactionDisabled ? 'opacity-50 cursor-not-allowed' : '',
              ].join(' ')}
            >
              <input
                id={checkboxId}
                type="checkbox"
                checked={hardBurnConfirmed}
                onChange={(e) => handleHardBurnConfirmedChange(e.target.checked)}
                disabled={interactionDisabled}
                className="h-4 w-4 accent-orange-500 flex-shrink-0"
              />
              <span className="text-sm text-white/80">
                {t('subtitles.hardBurn.confirmCheckbox', {
                  defaultValue:
                    'I understand the cost and confirm I want to burn subtitles into this video.',
                })}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
});
